// OGSBranch — a named sub-graph (head / body / hair / face) grouping a decoder
// chain. It "breaks into" its member decoder nodes (brokenInto) and marks a
// branch boundary: each branch decodes+lifts independently and the per-branch
// Gaussians are concatenated for ONE rasterizer call (per multi_part_gaussian_model.py).
// As a graph node it's a pass-through — the terminal UV outputs of its member
// chain flow through unchanged; grouping + concat is its real role.
//
// LIVE CONTAINER (the `live` option): the same node also OWNS one branch's
// runtime — the deform (rig→verts) + trunk→geom→appr (via OGSAvatar) + its own
// lifter and plücker. Every decoder output stays on the shared ORT device, the
// deform's
// `vertices` go straight into the lifter's vert buffer, and a bundle's
// *_fp16.onnx decoders are selectable.
//
// Init is deliberately TWO-PHASE, because the ordering is load-bearing:
//   init(ctx)      creates the ONNX sessions. ORT's WebGPU device does not exist
//                  yet, so ctx.device is null and only ctx.gpuBufferIo
//                  (from prepareOgsLiftDevice) decides the output hints.
//   initGpu(ctx,d) after every session exists: resolveOgsLiftDevice has ORT's
//                  real device, so the plücker is re-inited on it and the lifter
//                  is built on it.
// Inverting it (device first, lifter before the sessions) forces a CPU
// round-trip: the lifter ends up on a device the decoders' outputs don't live on.

import { OGSAvatar } from "./OGSAvatar.ts";
import { OrlDeform } from "./OrlDeform.ts";
import { OrlShellDeform } from "./OrlShellDeform.ts";
import { OGSRig2Mesh } from "./OGSRig2Mesh.ts";
import type { RigResolution } from "../../inference/rigMode.ts";
import { makeApprDecoder, makeGeomDecoder, makeTrunkDecoder } from "./decoderFactories.ts";
import { nodeTensorToLiftInput } from "../types.ts";
import { fp16NameFor } from "../../assets/sceneManifest.ts";
import { ogsGpuFlags, type OgsGpuFlags } from "../../inference/ogsGpuFlags.ts";
import type { LiftImpl } from "../../inference/myra/myraLiftGate.ts";
import { composeSimilarity } from "../../inference/poseAnchors.js";
import type { DecoderKind } from "../decoderBuild";
import type {
  DirtySource,
  FrameInputs,
  Node,
  NodeContext,
  NodeTensor,
  PortSpec,
} from "../types";
import type * as THREE from "three";
import type { Gaussians } from "../../inference/lift";
import type { BranchAssets } from "../../assets/loadMultiRegionScene";
import { captureRestPose, computeDisplacement } from "../../rig/headDisplacement.js";
import { buildHeadDispMap } from "../../rig/headDisplacementMap.js";
import type { PbrConfig } from "../../assets/pbrManifest.ts";
import { quaternionToMat3 } from "../../inference/sh27Rotate.ts";
import type { BsdfSessionLike } from "../../inference/NeuralBsdf.ts";
import type { PbrShadeLike } from "../../inference/pbrShadeParams.ts";

/** Structural slice of GaussianLifter / GpuLifter. Both are LAZY-imported (they
 *  pull `.wgsl?raw` modules that node's unit runner can't load), so the branch
 *  never names the classes — it tracks the resolved kind as a string instead. */
export interface BranchLifter {
  out: Gaussians;
  packedMode?: boolean;
  computeJacobians(verts: Float32Array): void;
  setPoseGpu?(verts: { gpuBuffer: GPUBuffer; byteLength: number }): void;
  /** Re-transform the vertices of the LAST setPoseGpu against the current anchor,
   *  from the lifter's own snapshot of them. False when it holds none yet. */
  reapplyPose?(): boolean;
  setVertCorrection?(corr: Float32Array | null): void;
  /** A ROW-major 3x3 applied as `v·M`, plus a translation. Optional because the
   *  CPU GaussianLifter has no transform pass — it takes vertices the branch has
   *  already transformed. */
  setDeformLinear?(lin: ArrayLike<number>, offset: ArrayLike<number>): void;
  /** The vertex buffer the jacobians read, for a consumer that skins from it. */
  readonly vertsBuffer?: GPUBuffer;
  setVertsGpu?(buffer: GPUBuffer, byteLength?: number): void;
  /** Skip the jacobian pass after a transform — set when a consumer replaces the
   *  vertices and runs it itself. */
  deferJacobians?: boolean;
  /** This branch's PBR shade stage, when its appr emits a material. Declared here
   *  so a lifter WRAPPER can forward it: the renderer drives the stage through
   *  whatever object it was handed, and a wrapper that hides it leaves the shade
   *  at P = 0, which writes nothing and renders the albedo logits. */
  readonly pbrShade?: PbrShadeLike | null;
  destroy?(): void;
}

/** Best-fit similarity (uniform scale + proper rotation + translation) taking
 *  `src` onto `dst`, returned ROW-major for the `v·M + offset` convention the
 *  WGSL pass and the server both use.
 *
 *  Kabsch, via the 3x3 cross-covariance — small enough to solve here rather than
 *  pull in a matrix library. A reflection is excluded (the det correction): a
 *  mirrored head is a broken asset, not a coordinate convention.
 *
 *  SAME DECOMPOSITION as server/orl/source.py's calibrate(), NOT the same solver:
 *  the server has numpy and calls `np.linalg.svd`, taking the scale from the
 *  singular values; this takes the rotation from the polar iteration below and
 *  the scale from trace(RᵀH)/Σ|a|², which is the same quantity written without an
 *  SVD. They agree by construction rather than by test, so do not "simplify"
 *  either one into a different fit. What is genuinely shared is the part that
 *  matters operationally: both refuse a reflection, and both refuse a residual
 *  too large to be the rig-vs-wrap gap (see calibrate()).
 *
 *  Eigen-decomposition of a symmetric 3x3 is avoided by taking the rotation from
 *  a few Newton iterations on the polar decomposition (M → (M + M⁻ᵀ)/2), which
 *  converges in ~5 steps for the near-rigid matrices this ever sees. */
export function fitSimilarityRows(src: Float32Array, dst: Float32Array): {
  lin: Float32Array; offset: Float32Array;
} {
  const n = src.length / 3;
  const cs = [0, 0, 0], cd = [0, 0, 0];
  for (let i = 0; i < src.length; i += 3) {
    cs[0] += src[i]; cs[1] += src[i + 1]; cs[2] += src[i + 2];
    cd[0] += dst[i]; cd[1] += dst[i + 1]; cd[2] += dst[i + 2];
  }
  for (let k = 0; k < 3; k++) { cs[k] /= n; cd[k] /= n; }

  // H = Σ aᵀb (a, b centred), and Σ|a|² for the scale.
  const H = new Float64Array(9);
  let aa = 0;
  for (let i = 0; i < src.length; i += 3) {
    const ax = src[i] - cs[0], ay = src[i + 1] - cs[1], az = src[i + 2] - cs[2];
    const bx = dst[i] - cd[0], by = dst[i + 1] - cd[1], bz = dst[i + 2] - cd[2];
    H[0] += ax * bx; H[1] += ax * by; H[2] += ax * bz;
    H[3] += ay * bx; H[4] += ay * by; H[5] += ay * bz;
    H[6] += az * bx; H[7] += az * by; H[8] += az * bz;
    aa += ax * ax + ay * ay + az * az;
  }

  // Polar decomposition of H: iterate R ← (R + R⁻ᵀ)/2 until orthogonal.
  //
  // A SINGULAR H means the points span fewer than three dimensions (all equal,
  // collinear, coplanar) and no rotation is determined by them. Fall back to the
  // identity rather than iterating on a degenerate matrix: the fit degrades to
  // scale + translation, which is the most those points can support. Real head
  // meshes never hit this; the neutral-vs-neutral fixtures in the unit tests do.
  let R = Float64Array.from(H);
  if (!invert3(R)) R = Float64Array.from([1, 0, 0, 0, 1, 0, 0, 0, 1]);
  for (let it = 0; it < 24; it++) {
    const inv = invert3(R);
    if (!inv) break;
    let delta = 0;
    for (let k = 0; k < 9; k++) {
      // transpose of the inverse, i.e. inv[col][row]
      const t = inv[(k % 3) * 3 + ((k / 3) | 0)];
      const next = 0.5 * (R[k] + t);
      delta += Math.abs(next - R[k]);
      R[k] = next;
    }
    if (delta < 1e-12) break;
  }
  // A reflection is refused, not repaired. Kabsch's usual fix (negate the
  // smallest singular direction) needs the SVD this polar iteration avoids, and
  // negating the whole matrix would drive the fitted SCALE negative instead —
  // silently mirroring the face. det < 0 means the neutral genuinely does not
  // correspond to the rig's (a mirrored or mis-ordered mesh), which is a broken
  // asset rather than a coordinate convention, so say so.
  if (det3(R) < 0) {
    throw new Error(
      "rig→checkpoint calibration: the bundle's neutral is a REFLECTION of the "
      + "rig's, which no rotation can reach — a mirrored or vertex-reordered mesh.");
  }

  // Uniform scale that best matches |a| to |b| under R: trace(Rᵀ H) / Σ|a|².
  let num = 0;
  for (let k = 0; k < 9; k++) num += R[k] * H[k];
  const scale = aa > 0 ? num / aa : 1;

  const lin = new Float32Array(9);
  for (let k = 0; k < 9; k++) lin[k] = scale * R[k];
  const offset = new Float32Array([
    cd[0] - (cs[0] * lin[0] + cs[1] * lin[3] + cs[2] * lin[6]),
    cd[1] - (cs[0] * lin[1] + cs[1] * lin[4] + cs[2] * lin[7]),
    cd[2] - (cs[0] * lin[2] + cs[1] * lin[5] + cs[2] * lin[8]),
  ]);
  return { lin, offset };
}

/** Mean distance of a point set from its own centroid — the scale a residual is
 *  judged against, so the tolerance means the same thing on a head as on a
 *  decimated eye shell. */
function meanRadius(p: Float32Array): number {
  const n = p.length / 3;
  if (!n) return 0;
  let cx = 0, cy = 0, cz = 0;
  for (let i = 0; i < p.length; i += 3) { cx += p[i]; cy += p[i + 1]; cz += p[i + 2]; }
  cx /= n; cy /= n; cz /= n;
  let sum = 0;
  for (let i = 0; i < p.length; i += 3) sum += Math.hypot(p[i] - cx, p[i + 1] - cy, p[i + 2] - cz);
  return sum / n;
}

function det3(m: ArrayLike<number>): number {
  return m[0] * (m[4] * m[8] - m[5] * m[7])
       - m[1] * (m[3] * m[8] - m[5] * m[6])
       + m[2] * (m[3] * m[7] - m[4] * m[6]);
}

function invert3(m: ArrayLike<number>): Float64Array | null {
  const d = det3(m);
  if (!isFinite(d) || Math.abs(d) < 1e-30) return null;
  const o = new Float64Array(9);
  o[0] = (m[4] * m[8] - m[5] * m[7]) / d;
  o[1] = (m[2] * m[7] - m[1] * m[8]) / d;
  o[2] = (m[1] * m[5] - m[2] * m[4]) / d;
  o[3] = (m[5] * m[6] - m[3] * m[8]) / d;
  o[4] = (m[0] * m[8] - m[2] * m[6]) / d;
  o[5] = (m[2] * m[3] - m[0] * m[5]) / d;
  o[6] = (m[3] * m[7] - m[4] * m[6]) / d;
  o[7] = (m[1] * m[6] - m[0] * m[7]) / d;
  o[8] = (m[0] * m[4] - m[1] * m[3]) / d;
  return o;
}

export interface OGSBranchLiveOptions {
  /** Branch mesh + ONNX filenames, straight off the scene manifest. */
  assets: BranchAssets;
  providers: string[];
  /** Mounted ONNX bytes by filename (the pack VFS). A missing REQUIRED file
   *  throws — never a stub session. Required unless the chain is injected
   *  (`avatar`), which is how the single-region bundle wires its FUSED geom. */
  getBytes?: (name: string) => Uint8Array | undefined;
  /** Lazily download a bundle file that was NOT eagerly fetched. Used for the
   *  fp32 geom/appr of a bundle that ships fp16 siblings: those are the fp16
   *  path's fallback only, so paying ~150 MB of cold-boot bandwidth for bytes
   *  no session is built from is pure waste. Rejects loudly when absent. */
  fetchBytes?: (name: string) => Promise<Uint8Array>;
  /** The Avatar node's fp16 pick. AND-ed with ?fp16 / ?geomfp16 / ?apprfp16. */
  preferFp16?: boolean;
  /** 1/worldScale — the plücker camera origin scale. */
  originScale?: number;
  worldOffset?: readonly number[];
  /** scene.json world_rotation as a quaternion. Reaches ONLY the plücker camera, and
   *  inverted: the decoders, the lift and the verts all stay in the trained frame —
   *  the rotation is the SplatMesh's (render/worldRotation.ts). */
  worldRotation?: THREE.Quaternion | null;
  /** The bundle's BecomingLit declaration, when this branch's appr emits a
   *  material. Passed in rather than read off a manifest, by the same rule as
   *  every other field here: a branch holds ONE region's spec. */
  pbr?: PbrConfig | null;
  /** "lift" too, not just a decoder: losing the WGSL lift is the same class of
   *  capability loss, and the same pill is where the user finds out. Likewise
   *  "pbr" — a branch that loses its shade stage paints logits as colour. */
  onDowngrade?: (kind: DecoderKind | "lift" | "pbr", reason: string) => void;
  flags?: OgsGpuFlags;
  /** The bundle's rig_names.json — the control-name list for this rig vector.
   *  Only needed by the exact rig, which maps those names onto the DNA's GUI
   *  controls. */
  rigNames?: string[];
  /** Which rig this bundle resolved to, decided by the loader before anything
   *  was downloaded (src/ogs/inference/rigMode.ts) — the branch cannot re-decide
   *  it, because only the loader knows which of the two rigs' bytes exist. */
  rigPlan?: RigResolution;
  /** Shared `GpuLifter` (default) or the myra fork. The CALLER decides, from the
   *  bundle's subject — a branch holds one region's spec, not the manifest. */
  lift?: LiftImpl;
}

/** MetaHuman head_lod0 vertex count — what an ORL bundle bakes. A branch mesh
 *  of any other size is not a head_lod0 and can't be driven by ORL. */
const ORL_HEAD_VERTS = 24049;

/** Vertex ceiling for the decimated eyes/teeth stand-in the rigid shells drive.
 *  Measured: eyeline 762, isaac 882; a real body/clothes branch is 32k-165k.
 *
 *  A size test, not a name test, because the eyes/teeth branch is ITSELF named
 *  'body' on eyeline while myra's 'body' is a real body. Above this the branch
 *  keeps its mesh vertices, which is where partDriver's skinned verts land. */
export const ORL_SHELL_MAX_VERTS = 4096;

/** What OGSBranch needs from a rig→verts node, satisfied by BOTH the distilled
 *  historical ONNX rig2mesh and the exact-rig OrlDeform. */
export interface BranchDeform {
  init(ctx: NodeContext): Promise<void>;
  /** Phase 2 (post-device) hook — only the ORL node needs it, to build its WGSL
   *  deform on the SAME device the lifter got. */
  initGpu?(device: GPUDevice | null): Promise<void>;
  pose(rig: Float32Array): NodeTensor | Promise<NodeTensor>;
  runCpu(rig: Float32Array): Promise<Float32Array>;
  download(t: NodeTensor): Promise<Float32Array>;
  readonly activeSession: { setDownloadOutputs(on: boolean): void } | null;
  dispose(): void;
}

export interface OGSBranchOptions {
  id: string;
  members: string[]; // decoder/encoder node ids this branch groups
  ports?: string[]; // terminal outputs passed through (default geom_uv + color_uv)
  /** Scene-manifest branch name (head / body). Defaults to `id`. */
  name?: string;
  /** Present → this branch owns a live inference chain (see the header). */
  live?: OGSBranchLiveOptions;
  /** Inject the live chain instead of building it from the branch's ONNX
   *  filenames (mirrors OGSAvatar's own geom/appr/plucker injection). Used by
   *  tests AND by the single-region bundle, whose FUSED geom (rig_params →
   *  geom_uv + code, no trunk) and lazily-fetched fp32 sources don't come off a
   *  BranchSpec. `live.assets` is still required — it carries the mesh + dims. */
  avatar?: OGSAvatar;
  deform?: BranchDeform | null;
}

/** Same control vector, element for element. Cheap: 168 floats against a 5 ms deform. */
function sameRig(a: Float32Array, b: Float32Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

export class OGSBranch implements Node {
  readonly id: string;
  readonly name: string;
  readonly type = "ogsBranch";
  readonly inputs: readonly PortSpec[];
  readonly outputs: readonly PortSpec[];
  readonly bindings = [];
  readonly members: readonly string[];

  // --- live container state (all null/0 for a pure grouping node) ------------
  private readonly live: OGSBranchLiveOptions | null;
  private readonly flags: OgsGpuFlags;
  private avatar: OGSAvatar | null = null;
  private deform: BranchDeform | null = null;

  /** The DNA's RAW control layer, when THIS branch is skinned by the exact rig.
   *  Null on every other deform (rig2mesh, the rigid shells, none) — the raw
   *  layer is a property of RigLogic, not of posing in general. */
  get rawLayer(): unknown {
    return (this.deform as { rawLayer?: unknown } | null)?.rawLayer ?? null;
  }
  private corr: Float32Array | null = null;
  // The rig→checkpoint anchoring, folded into the lifter's ONE vert-transform
  // pass:  v' = v·M + offset + (corr ? corr[i] : 0).  Both terms are fitted by
  // calibrate(); M is ROW-major, applied as `v·M`, and stays identity until it
  // has run.
  private anchorLin: Float32Array = new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]);
  private anchorOffset = new Float32Array(3);
  // What calibrate() fitted, kept UNMODIFIED. `anchorLin`/`anchorOffset` above are
  // the LIVE pair and move with the baked per-pose delta (setPoseAnchorDelta), so
  // every delta has to compose onto the row-0 fit rather than onto last frame's
  // result — otherwise the deltas accumulate and the head walks away.
  private baseAnchorLin: Float32Array = new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]);
  private baseAnchorOffset = new Float32Array(3);
  private anchored = false;
  /** Why calibrate() refused this branch's rig, or null. Read by OGSModel, which
   *  knows the slug and owns the UI report. */
  calibrationError: string | null = null;
  private warnedVertCount = false;
  private lastGeom: NodeTensor | null = null;
  private lastColor: NodeTensor | null = null;
  private _liftKind = "none";
  private warnedCpuVerts = false;
  /** Sticky demote state (see setDownloadOutputs) — re-applied after any session
   *  swap, since `downloadOutputs` lives on the DecoderSession, not the node. */
  private downloadOutputs = false;
  /** Pose vertices the lift is anchored to (the renderer's getVerts). Own
   *  buffer — the mesh's neutralVertices must never be mutated. On the
   *  gpu-buffer pose path the verts never come back to the CPU, so this stays
   *  at the neutral and only the lifter's on-device bufVerts moves. */
  verts: Float32Array = new Float32Array(0);
  /** Rest pose the head displacement is measured against -- the branch's own
   *  neutral vertices, captured once at construction. Only set when the bundle
   *  supplied a mesh; null leaves headDispTensor() returning null, which is the
   *  normal state for every rig-conditioned branch. */
  private restVerts: Float32Array | null = null;
  private dispBuf: Float32Array | undefined = undefined;
  private reportedDispGpu = false;
  /** Reused NCHW (1,3,H,W) buffer for the local_disp head's disp_map. */
  private dispMapBuf: Float32Array | undefined = undefined;
  /** The disp_map the last runGeom decoded from (local_disp head only) — the
   *  parity harness and `?meshview`-style diagnostics read it; null otherwise. */
  lastDispMap: Float32Array | null = null;
  /** The rig time this branch's verts were last posed at (partBodyPose.js stamps
   *  it), so the pipeline can tell when the head lags the body. Null until posed. */
  lastPosedAt: number | null = null;
  lifter: BranchLifter | null = null;
  /** Skin this branch's deform output by the BODY rig, on device — set by the part drive.
   *
   *  A live branch owns its vertices, which is why the body normally reaches it as a rigid
   *  delta on its anchor. But a branch that also has a part in `rig.glb` can be skinned
   *  properly instead: the deform gives the expression, the skinning gives the carry, and
   *  they compose the way a face rig and a body rig always do. Run at the END of a pose,
   *  reading the vertices the transform pass just produced. */
  bodySkin: (() => void) | null = null;

  constructor(opts: OGSBranchOptions) {
    this.id = opts.id;
    this.name = opts.name ?? opts.id;
    this.members = opts.members;
    const ports = opts.ports ?? ["geom_uv", "color_uv"];
    this.inputs = ports.map((name) => ({ name }));
    this.outputs = ports.map((name) => ({ name }));
    this.live = opts.live ?? null;
    this.flags = opts.live?.flags ?? ogsGpuFlags();
    if (this.live) {
      this.verts = new Float32Array(this.live.assets.mesh.neutralVertices);
      // Captured BEFORE anything poses `verts`, and never re-captured: the
      // trained encoder measured displacement against the mesh's frame-0 rest
      // pose, so re-basing it on a later frame would silently shift every code.
      this.restVerts = captureRestPose(this.verts);
      if (opts.avatar) {
        this.avatar = opts.avatar;
        this.deform = opts.deform ?? null;
      } else {
        this.buildNodes(this.live);
      }
    }
  }

  // Phase 1: the ONNX sessions (ORT's device doesn't exist yet — see the header).
  async init(ctx: NodeContext): Promise<void> {
    if (!this.avatar) return;
    // The deform only keeps its verts on a gpu-buffer when the whole chain does
    // AND ?gpudeform is on; otherwise it must hand back a CPU array.
    if (this.deform) {
      try {
        await this.deform.init({ ...ctx, gpuBufferIo: ctx.gpuBufferIo && this.flags.gpuDeform });
      } catch (e) {
        console.warn(`[ogs] branch '${this.name}': deform model failed to load`, e);
        // Dispose before dropping it: a partly-built deform still owns a claim on
        // the shared ORL rig (and possibly GPU buffers), and simply reassigning
        // the field leaks both for the life of the page.
        try { this.deform.dispose(); } catch { /* best-effort */ }
        this.deform = null;
        // Try the distilled rig before giving up — the branch's own asset, just
        // not one worth downloading unless this happens.
        if (this.live) {
          this.deform = await this.makeLazyRig2Mesh(this.live);
          if (this.deform) {
            try {
              await this.deform.init({ ...ctx, gpuBufferIo: ctx.gpuBufferIo && this.flags.gpuDeform });
            } catch (e2) {
              console.warn(`[ogs] branch '${this.name}': the rig2mesh fallback failed to load too — neutral mesh`, e2);
              this.deform = null;
            }
          } else {
            console.warn(`[ogs] branch '${this.name}': no rig2mesh fallback available — falling back to neutral mesh`);
          }
        }
      }
    }
    await this.avatar.init(ctx);
  }

  // Build the live chain: the deform + trunk→geom→appr. The three decoders are
  // built HERE (not by OGSAvatar's own defaults) because only here do we know
  // the branch's fp16 siblings.
  /** The bundle-level NeuralBSDF, if it shipped one. Its reflectance is the
   *  diffuse's ~5x term on myra, so a branch without it is dark rather than
   *  merely approximate — hence a downgrade report rather than silence. */
  private async attachNeuralBsdf(
    shade: { featureBuffer: GPUBuffer; reflectanceBuffer: GPUBuffer; rows: number;
             setNeuralBsdf(b: unknown): void; setReflectanceReady(r: boolean): void },
    pbr: PbrConfig, device: GPUDevice, ctx: NodeContext,
  ): Promise<void> {
    const live = this.live;
    if (!live) return;
    try {
      const bytes = live.getBytes?.(pbr.bsdf)
        ?? (live.fetchBytes ? await live.fetchBytes(pbr.bsdf) : undefined);
      if (!bytes) throw new Error(`the bundle does not carry '${pbr.bsdf}'`);
      const { createSession } = await import("../../../lib/ortSession.js");
      const { withOrtGate } = await import("../../../lib/ortGate.js");
      const { NeuralBsdf } = await import("../../inference/NeuralBsdf.ts");
      // Gated, like every other ORT op on the shared runtime (src/lib/ortGate.js).
      const session: BsdfSessionLike = await withOrtGate(
        () => createSession(ctx.ort, bytes, live.providers ?? []));
      shade.setNeuralBsdf(new NeuralBsdf({
        device, session, rows: shade.rows, featDim: pbr.featDim,
        // The SAME gate every decoder run goes through. This one is issued off
        // the decode chain, so without it the two overlap.
        gate: withOrtGate,
        featureBuffer: shade.featureBuffer, reflectanceBuffer: shade.reflectanceBuffer,
        makeTensor: (type, data, dims) => new ctx.ort.Tensor(type, data, dims),
        onReady: (ready) => shade.setReflectanceReady(ready),
        onError: (message) => {
          console.error(`[ogs] branch '${this.name}': NeuralBSDF run failed — `
            + "shading at refl = 1, which is a fraction of the diffuse", message);
          live.onDowngrade?.("pbr", `NeuralBSDF: ${message}`);
        },
      }));
    } catch (e) {
      console.error(`[ogs] branch '${this.name}': no NeuralBSDF — the diffuse is `
        + "albedo * irradiance with the reflectance term dropped", e);
      live.onDowngrade?.("pbr", `NeuralBSDF unavailable: ${(e as Error)?.message ?? String(e)}`);
    }
  }

  private buildNodes(live: OGSBranchLiveOptions): void {
    const { assets, providers, getBytes, onDowngrade } = live;
    const spec = assets.spec;
    if (!getBytes) throw new Error(`OGSBranch "${this.id}": live.getBytes is required unless the chain is injected`);
    const req = (file: string): Uint8Array => {
      const b = getBytes(file);
      // Loud, never a stub session (CLAUDE.md: no silent fallback to fake data).
      if (!b) throw new Error(`multi-region: ONNX bytes missing for '${file}'`);
      return b;
    };
    // fp16 siblings are OPTIONAL: a bundle without them loads fp32, with no
    // downgrade report.
    const fp16 = (file: string): Uint8Array | undefined => {
      const n = fp16NameFor(file);
      return n ? getBytes(n) : undefined;
    };
    // fp32 source for a decoder that HAS an fp16 sibling: those bytes are only
    // reached when the fp16 build fails, so the pack leaves them unfetched and
    // hands us a lazy downloader instead. Already-resident bytes win (a bundle
    // with no fp16 sibling fetched them eagerly).
    const fp32Src = (file: string): { bytes: Uint8Array } | { getFp32: () => Promise<Uint8Array> } => {
      const b = getBytes(file);
      if (b) return { bytes: b };
      if (live.fetchBytes) return { getFp32: () => live.fetchBytes!(file) };
      return { bytes: req(file) }; // throws loudly — never a stub session
    };
    // fp16-internal decoders are a WebGPU asset, so gate on the EP. Without
    // this, ?ep=wasm builds the fp16 .onnx on WASM and decoderResolve's
    // 'no-webgpu' downgrade report is unreachable, so the user either silently
    // runs half-precision conv on wasm or sees the wrong reason.
    const onWebgpu = providers[0] === "webgpu";
    const want16 = !!live.preferFp16 && onWebgpu;
    const common = { providers, onDowngrade };
    // FUSED branch (a single-region bundle, adapted by assets/sceneManifest.ts's
    // singleRegionScene): no trunk ONNX exists — geom takes `rig_params` straight
    // in and emits `code` + `geom_uv` itself. Everything downstream (plücker,
    // appr, lifter, gpu-buffer io, fp16) is identical, which is why a
    // single-region bundle needs no pipeline of its own.
    // LOCAL_DISP head (TexAvatars --head_local_disp, myra_v16): no trunk either, but
    // NOT fused — geom takes the per-texel displacement UV map this branch
    // rasterises from its posed vertices (headDispMapTensor), appr takes plücker +
    // that same map, and the code is a constant baked into both graphs at export.
    // A trunk alongside it is a contradiction, refused rather than half-built.
    const localDisp = spec.headCond === "local_disp";
    if (localDisp && spec.trunk) {
      throw new Error(`OGSBranch "${this.id}": a local_disp head declares trunk '${spec.trunk}' — its code is baked into geom/appr, so there is nothing for a trunk to produce`);
    }
    const fusedGeom = !localDisp && (spec.fused === true || !spec.trunk);
    if (fusedGeom && spec.trunk) {
      throw new Error(`OGSBranch "${this.id}": branch is fused but also declares trunk '${spec.trunk}'`);
    }
    this.avatar = new OGSAvatar({
      id: `${this.id}.avatar`,
      originScale: live.originScale ?? 1,
      worldOffset: live.worldOffset,
      worldRotation: live.worldRotation,
      providers,
      dispMode: localDisp,
      ...(fusedGeom || localDisp ? {} : {
        trunk: makeTrunkDecoder({
          ...common, id: `${this.id}.trunk`, label: `${this.name}.trunk`, bytes: req(spec.trunk as string),
        }),
      }),
      geom: makeGeomDecoder({
        ...common, id: `${this.id}.geom`, label: `${this.name}.geom`,
        input: localDisp ? "disp_map" : fusedGeom ? "rig_params" : "code",
        ...fp32Src(spec.geom), fp16Bytes: fp16(spec.geom), want16: want16 && this.flags.geomFp16,
      }),
      appr: makeApprDecoder({
        ...common, id: `${this.id}.appr`, label: `${this.name}.appr`, dispMap: localDisp,
        viewIndependent: spec.pbr === true,
        ...fp32Src(spec.appr), fp16Bytes: fp16(spec.appr), want16: want16 && this.flags.apprFp16,
      }),
    });
    this.deform = this.makeDeform(live);
  }

  /** This branch's rig→vertices node, or null for "no rig" (the branch renders
   *  at its neutral mesh).
   *
   *  The bundle already chose WHICH rig, before anything was downloaded
   *  (inference/rigMode.ts) — so this only maps that choice onto one branch:
   *
   *    'rig2mesh' → the distilled per-branch ONNX, the fallback rig.
   *    'orl'      → the exact MetaHuman rig, as skinning where the branch mesh
   *                 IS head_lod0, as rigid shells where it isn't (a `head.dna`
   *                 carries head, teeth and eyes, so a multi-region bundle's
   *                 eyes/teeth branch is still driven — see OrlShellDeform —
   *                 while a genuine body or hair branch is not).
   *    'none'     → null.
   *
   *  `null` is expected and survivable; a bad identity or a corrupt bundle still
   *  fails loudly in init(). A character with NO rig at all is reported to the UI
   *  by the loader (reportMissingRig), because a motionless FACE is not
   *  survivable silently — it looks exactly like a hung decoder. */
  private makeDeform(live: OGSBranchLiveOptions): BranchDeform | null {
    const plan = live.rigPlan;
    if (!plan || plan.rig === "none") return null;
    if (plan.rig === "rig2mesh") return this.makeRig2Mesh(live);
    const controlNames = live.rigNames;
    if (!controlNames?.length) {
      console.warn(`[ogs] branch '${this.name}': no rig_names.json in the bundle — branch stays at its neutral pose`);
      return null;
    }
    const verts = live.assets.mesh.neutralVertices.length / 3;
    if (!live.getBytes) {
      console.warn(`[ogs] branch '${this.name}': ORL rig not mounted — branch stays at its neutral pose`);
      return null;
    }
    if (verts !== ORL_HEAD_VERTS) {
      // Not head_lod0, so the DNA cannot skin it directly — but that does NOT
      // mean the DNA has nothing to say about it. The second branch of a
      // multi-region bundle is the character's EYES AND TEETH, decimated to a
      // per-character stand-in, and those parts are rigid bone the rig moves
      // through four joints. Drive them as rigid shells (OrlShellDeform).
      // A body, clothes or hair branch is none of those, and the eye/teeth joints
      // exist in every head.dna — so OrlShellDeform's own guards can't refuse it,
      // and it would split a body into shells and pose them off the teeth joint.
      if (verts > ORL_SHELL_MAX_VERTS) {
        console.log(`[ogs] branch '${this.name}': ${verts} verts is neither head_lod0 nor the eyes/teeth shells — vertices come from its mesh`);
        return null;
      }
      console.log(`[ogs] branch '${this.name}': ${verts} verts is not MetaHuman head_lod0 (${ORL_HEAD_VERTS}) — trying the rigid-shell rig`);
      return new OrlShellDeform({
        id: `${this.id}.orlShellDeform`,
        getBytes: live.getBytes,
        controlNames,
        neutralVertices: live.assets.mesh.neutralVertices,
        faces: live.assets.mesh.faces,
      });
    }
    console.log(`[ogs] branch '${this.name}': driving with the exact MetaHuman rig from this character's assets`);
    return new OrlDeform({
      id: `${this.id}.orlDeform`,
      getBytes: live.getBytes,
      controlNames,
      expectVerts: verts,
      // ?gpudeform=0 keeps the deform on the CPU.
      gpuDeform: this.flags.gpuDeform,
    });
  }

  /** The FALLBACK rig: this branch's distilled rig2mesh ONNX, from bytes the
   *  loader already fetched. Null when the branch declares none — a bundle whose
   *  head has a rig2mesh and whose body doesn't is normal, and the body then
   *  renders neutral exactly as it always has. */
  private makeRig2Mesh(live: OGSBranchLiveOptions): BranchDeform | null {
    const file = live.assets.spec.rig2mesh;
    const bytes = file ? live.getBytes?.(file) : undefined;
    if (!file || !bytes) {
      console.warn(`[ogs] branch '${this.name}': no rig2mesh in this bundle — branch stays at its neutral pose`);
      return null;
    }
    console.log(`[ogs] branch '${this.name}': driving with the distilled rig2mesh (${file})`);
    return new OGSRig2Mesh({
      id: `${this.id}.rig2mesh`, providers: live.providers, bytes,
    });
  }

  /** The exact rig failed to build. Pull this branch's rig2mesh ON DEMAND and use
   *  it instead — the bytes were deliberately not fetched at boot, which is what
   *  keeps the fallback free for every character whose rig works.
   *
   *  Refused when the bundle's plan says so (`?rig=orl`, or no loadable rig2mesh
   *  to fetch): a forced rig that quietly hands back the other one is worse than
   *  a frozen head, because the frozen head is at least visible. */
  private async makeLazyRig2Mesh(live: OGSBranchLiveOptions): Promise<BranchDeform | null> {
    const file = live.assets.spec.rig2mesh;
    if (!live.rigPlan?.lazyFallback || !file || !live.fetchBytes) return null;
    try {
      console.warn(`[ogs] branch '${this.name}': falling back to the distilled rig2mesh (${file}) — downloading it now`);
      const bytes = await live.fetchBytes(file);
      return new OGSRig2Mesh({ id: `${this.id}.rig2mesh`, providers: live.providers, bytes });
    } catch (e) {
      // Both rigs are now unavailable. Loud, then neutral — never a stub.
      console.warn(`[ogs] branch '${this.name}': the rig2mesh fallback could not be fetched either`, e);
      return null;
    }
  }

  // Phase 2: the plücker + lifter, on the device ORT actually built.
  // `ctx.device` is the page's one device (the renderer's, ORT's and the lift's)
  // or null with no WebGPU; `liftDevice` is the same device, or null for the CPU
  // lifter. They were allowed to differ when ORT built its own device and the page
  // could not give it one — see OgsLiftMode.
  async initGpu(
    ctx: NodeContext,
    liftDevice: GPUDevice | null,
    opts: { packed?: boolean } = {},
  ): Promise<void> {
    if (!this.live || !this.avatar) return;
    // One GpuPlucker PER BRANCH at that branch's uv_res: a single shared
    // instance destroys + rebuilds its buffer whenever the dims change, and
    // head (≈750) / body (256) alternate every frame.
    await this.avatar.pluckerNode.init(ctx);
    const mesh = this.live.assets.mesh;
    // Both live outside the ONNX — the trainer biases then clamps the geom decoder's
    // log-scale, so the lift has to. Absent on an injected/neutral branch that carries
    // no BranchSpec; 0 / no ceiling is the pre-v5 behaviour, so an older bundle lifts
    // exactly as it did.
    const scaleLogBias = this.live.assets.spec?.scaleLogBias ?? 0;
    const scaleLogMax = this.live.assets.spec?.scaleLogMax ?? Infinity;
    // Triangle-bounded clamp, from the bundle. Same contract as the three above:
    // the trainer applies it AFTER the decoder, so it is not in the ONNX and a
    // branch that lifts without it paints splats the checkpoint never had. 0 is
    // OFF, which is what a pre-clamp bundle gets and is byte-identical.
    const triKappa = this.live.assets.spec?.triKappa ?? 0;
    const sliverQMin = this.live.assets.spec?.sliverQMin ?? 0;
    const uvErode = this.live.assets.spec?.uvErode ?? 0;
    // The BOUNDED scale map (myra v8+). Same out-of-ONNX contract as the three
    // above, and the one whose absence is hardest to see: the bounded and
    // additive maps agree exactly at raw = 0, so a branch that ignores it looks
    // right at the neutral scale and inflates every splat above it.
    const sigmaMax = this.live.assets.spec?.sigmaMax ?? 0;
    const sigmaOffset = this.live.assets.spec?.sigmaOffset ?? 0;
    // The resolution the decoders EMIT at. When below uvRes the lift applies the
    // export's terminal Resize itself (uvResize.ts) — same texels, same filter,
    // minus the ORT tail. 0 = same as uvRes, which is every pre-decoder_res bundle.
    const decoderRes = this.live.assets.spec?.decoderRes ?? 0;
    const decoderLayout = this.live.assets.spec?.decoderLayout ?? "nchw";
    // Why the GPU lift was lost, for the downgrade report below. Null = we got it.
    let liftLoss: string | null = liftDevice ? null : "no WebGPU compute device";
    // A drop-in: same constructor, only pass 1 differs. Lazy-imported like the shared one.
    const wantMyraLift = this.live.lift === "myra";
    // The numbers the lift runs on, as the manifest PARSE handed them over. The
    // fork's deltas are all gated on these, so a zero here is a delta not applied.
    if (wantMyraLift) {
      console.info(`[ogs] branch '${this.name}': lift params scale_log_bias=${scaleLogBias} `
        + `scale_log_max=${scaleLogMax} tri_size_cap=${triKappa} sliver_min_quality=${sliverQMin} `
        + `uv_erode=${uvErode} sigma_max=${sigmaMax} sigma_offset=${sigmaOffset}`);
    }
    if (liftDevice) {
      try {
        // The appr's OWN width. A PBR branch emits a material, and the lift's
        // colour buffer has to hold all of it or the decode is copied short.
        const apprChannels = this.live.assets.spec?.apprChannels ?? 3;
        const args = [mesh, scaleLogBias, scaleLogMax, triKappa, sliverQMin,
                      uvErode, sigmaMax, sigmaOffset] as const;
        const l = wantMyraLift
          ? new (await import("../../inference/myra/MyraLifter.ts")).MyraLifter(
              liftDevice, ...args, undefined, apprChannels, decoderRes, decoderLayout)
          : new (await import("../../inference/GpuLifter.ts")).GpuLifter(
              liftDevice, ...args, undefined, apprChannels, decoderRes, decoderLayout);
        // packedMode: lift() reads back pass2's sparkjs packed words and
        // SplatMeshUpdater mutates ONE SplatMesh in place — no PLY rebuild, and
        // a FIXED index→splat map so Spark's async depth sort stays valid.
        // Opt OUT (packed:false) when the caller post-processes the SoA fields —
        // the myra readback drive culls over-stretched splats per branch, which a
        // packed lift leaves stale.
        l.packedMode = opts.packed !== false;
        // A PBR branch's appr emits a MATERIAL, so the lift's colour words are
        // logits until this stage turns them into radiance.
        const pbrConfig = this.live.pbr ?? null;
        if (pbrConfig && this.live.assets.spec?.pbr) {
          try {
            const { loadFgLut } = await import("../../inference/PbrShade.ts");
            // The trained light is authored in the TRAINING frame and the shade
            // reads world-space normals; worldRotation.ts's own rule — "both
            // halves or neither" — applies to the light as much as to the mesh
            // and the plücker camera.
            const shade = l.attachPbr(pbrConfig, await loadFgLut(),
              this.live.worldRotation ? quaternionToMat3(this.live.worldRotation) : null,
              `branch '${this.name}'`);
            await this.attachNeuralBsdf(shade, pbrConfig, liftDevice, ctx);
            console.info(`[ogs] branch '${this.name}': PBR shade armed `
              + `(${pbrConfig.apprChannels} channels, feat ${pbrConfig.featDim}, `
              + `light ${shade.lightSource} in ${shade.report().lightFrame}, `
              + `bsdf ${shade.neuralBsdf ? pbrConfig.bsdf : "MISSING — refl = 1"})`);
            // One seam per branch: which light each is shading with, and how
            // bright it is. A dark PBR branch beside a bright RGB one is the
            // expected look of a dim room, and only these numbers separate that
            // from a broken light.
            if (typeof window !== "undefined") {
              const w = window as unknown as { __OGS_PBR__?: Record<string, unknown> };
              w.__OGS_PBR__ = w.__OGS_PBR__ ?? {};
              w.__OGS_PBR__[this.name] = shade;
            }
          } catch (e) {
            // The material is unreadable without it, so the branch would paint
            // albedo logits as colour. Loud, and the branch renders unshaded.
            console.error(`[ogs] branch '${this.name}': PBR shade unavailable — `
              + `rendering the material's raw channels`, e);
            this.live.onDowngrade?.("pbr", (e as Error)?.message ?? String(e));
          }
        }
        this.lifter = l as unknown as BranchLifter;
        // A lift device that is NOT the page's can no longer arise by design, so
        // this reports it rather than treating it as an ordinary second mode.
        const device = liftDevice === ctx.device ? "page" : "separate";
        this._liftKind = `GPU(${device}${wantMyraLift ? ",myra" : ""})`;
      } catch (e) {
        const which = wantMyraLift ? "MyraLifter" : "GpuLifter";
        liftLoss = `${which} build failed: ${(e as Error)?.message ?? e}`;
        console.warn(`[ogs] branch '${this.name}': ${which} unavailable, staying on CPU lift`, e);
      }
    }
    if (!this.lifter) {
      const { GaussianLifter } = await import("../../inference/lift.ts");
      this.lifter = new GaussianLifter(mesh, scaleLogBias, scaleLogMax,
                                       triKappa, sliverQMin, uvErode,
                                       sigmaMax, sigmaOffset, decoderRes) as unknown as BranchLifter;
      this._liftKind = "CPU";
      // ?gpulift=0 is a choice; any other CPU lift is a capability loss the user must SEE —
      // it costs seconds per frame, and unseen it reads as "the avatar is just slow".
      if (this.flags.gpuLift) {
        console.error(`[ogs] branch '${this.name}': lifting on the CPU — ${liftLoss}`);
        this.live.onDowngrade?.("lift", liftLoss ?? "unknown");
      }
      // No fork/shared split to report here: GaussianLifter is one implementation
      // and it applies uv_erode, the sliver cull and the widened clamp gate off
      // the same manifest values the fork reads. Only speed is lost.
    }
    // The ORL deform must live on the device the LIFTER actually got (shared,
    // own, or none) — its output is copyBufferToBuffer'd into bufVerts, which is
    // invalid across devices.
    //
    // Gated on the resolved lift KIND, not on `this.lifter` being truthy: the
    // GpuLifter construction above can throw and fall through to the CPU
    // GaussianLifter, which is still a lifter but has no setPoseGpu. Passing the
    // device anyway built the WGSL deform, so every pose() came back as a
    // gpu-buffer the CPU lifter could not take, and the fallback downloaded it —
    // a staging allocation and a mapAsync queue drain PER FRAME, on the path
    // OrlDeform's own comment marks as never-per-frame. Rare (it needs GpuLifter
    // to fail) and silent (it only reads as "slow"), which is the failure shape
    // this repo documents against.
    const liftIsGpu = this._liftKind.startsWith("GPU");
    await this.deform?.initGpu?.(liftIsGpu ? liftDevice : null);

    // A branch without a deform never poses, so its jacobians are computed once.
    this.lifter.computeJacobians(this.verts);
  }

  /** One-time per-vertex anchor: corr = baked neutral − M·deform(baseRig).
   *  A rig→verts step is noisy in absolute position relative to the baked mesh;
   *  anchoring to it keeps the splat field coherent (a scalar mean offset
   *  scatters splats).
   *
   *  This is a DELIBERATE, init-only GPU→CPU readback — the only one on the rig
   *  path. Afterwards the correction lives in the lifter's transform pass and the
   *  verts never touch the CPU again. No-op without a deform.
   *
   *  Returns false when the branch will NOT be posed — no deform, a vertex-count
   *  mismatch, or a fit whose residual says this rig is not this mesh's. In the
   *  last case `calibrationError` carries the reason for the UI.
   *
   *  `rigSpace` is a sibling's fitted frame, for a branch that reports
   *  `needsRigSpace`. It is a PARAMETER rather than a separate call because the
   *  two have to happen in that order and nothing enforced it: adopting after
   *  calibrating placed the shells against a frame that had already been used,
   *  silently, and the only symptom was eyes and teeth in the wrong place. */
  async calibrate(
    baseRig: Float32Array,
    rigSpace: { lin: Float32Array; offset: Float32Array } | null = null,
  ): Promise<boolean> {
    if (!this.deform) return false;
    if (rigSpace && this.deform instanceof OrlShellDeform) {
      this.deform.adoptRigSpace(rigSpace.lin, rigSpace.offset);
    }
    const pred = await this.deform.runCpu(baseRig);
    const neutral = this.neutralVertices;
    if (pred.length !== neutral.length) {
      console.warn(`[ogs] branch '${this.name}': deform verts ${pred.length / 3} ≠ mesh ${neutral.length / 3} — live pose disabled`);
      this.deform.dispose();
      this.deform = null;
      return false;
    }
    // TWO terms, and neither substitutes for the other.
    //
    // The similarity is the change of FRAME: the exact rig emits its own rig
    // world space while TexAvatars bakes `100 · [GlobalRigid(t) ∘ rig2mesh_full]`,
    // and for isaac that rotation lived inside the shipped ONNX as `100·R0`
    // (measured 3.45°). Left out, every DISPLACEMENT is rotated wrong by ~6% of
    // its magnitude while the neutral still looks perfect — the per-vertex term
    // hides it at exactly the pose you check.
    //
    // The per-vertex corr is the rig-vs-WRAP shape gap: `neutral_vertices` is a
    // TRACKED mesh the rig only approximates (0.265 cm mean on isaac-ogs), which
    // no rigid transform reaches. Without it the browser renders the rig's
    // surface rather than the one the decoders were trained on.
    //
    // Same decomposition, same order, as server/orl/source.py's calibrate().
    //
    // The fit itself can refuse outright: a neutral that is a REFLECTION of the
    // rig's is not reachable by any rotation. That throw has to land in the same
    // place as the residual verdict below — it is the same verdict, reached
    // earlier — or a mirrored mesh takes the whole pipeline down instead of
    // freezing one branch.
    // Did this branch fit its own frame, or was it handed one? The guard below is
    // only meaningful for the former. Keyed on the ARGUMENT rather than on the
    // deform's class: being handed a frame is exactly what "did not fit it" means,
    // and the caller is the only thing that knows.
    const borrowedFrame = rigSpace !== null;
    let lin: Float32Array, offset: Float32Array;
    try {
      ({ lin, offset } = fitSimilarityRows(pred, neutral));
    } catch (e) {
      this.calibrationError = `${(e as Error).message} — wrong DNA for this character, or a vertex-order mismatch`;
      console.error(`[ogs] branch '${this.name}': ${this.calibrationError} — live pose disabled`);
      this.deform.dispose();
      this.deform = null;
      return false;
    }
    const corr = new Float32Array(pred.length);
    let residSum = 0;
    for (let i = 0; i < corr.length; i += 3) {
      const x = pred[i], y = pred[i + 1], z = pred[i + 2];
      corr[i]     = neutral[i]     - (x * lin[0] + y * lin[3] + z * lin[6] + offset[0]);
      corr[i + 1] = neutral[i + 1] - (x * lin[1] + y * lin[4] + z * lin[7] + offset[1]);
      corr[i + 2] = neutral[i + 2] - (x * lin[2] + y * lin[5] + z * lin[8] + offset[2]);
      residSum += Math.hypot(corr[i], corr[i + 1], corr[i + 2]);
    }
    // JUDGE THE RESIDUAL BEFORE CANCELLING IT. `corr` absorbs anything at the
    // anchor pose — including the wrong DNA, or head_lod0's vertex count in a
    // different ORDER — so an unchecked fit renders a perfect neutral face with
    // every expression driven by a rig that is not this mesh's.
    //
    // The threshold is a fraction of the mesh's own radius, not an absolute,
    // because some residual is legitimate (the tracked-wrap gap above). A
    // vertex-order mismatch lands near 100% of that radius, so 5% catches it with
    // room to spare. Same test and tolerance as the server's calibrate(), so the
    // two halves agree on what they REFUSE and not just on the maths they run.
    //
    // ONLY for a branch that fitted this frame ITSELF. A branch handed a sibling's
    // frame (the eyes/teeth shells) has no fit to judge: its mesh is a decimated
    // stand-in with no correspondence to the pack, so its residual measures how
    // well the HEAD's frame happens to place a different mesh — which is expected
    // to be worse and says nothing about whether the DNA is this character's.
    // Judging it here refused a branch whose shells bind and drive correctly
    // (measured on eyeline-v10c: jaw 0→1 moves exactly the 60 lower-teeth verts by
    // 2.74 cm), leaving the eyes and teeth frozen while the head worked. The head
    // is where a wrong DNA shows up, and the head is still guarded.
    const n = pred.length / 3;
    const resid = residSum / n;
    const tol = 0.05 * meanRadius(neutral);
    if (!borrowedFrame && tol > 0 && resid > tol) {
      // Freeze rather than render a face this rig does not fit, and say which of
      // the two causes to go looking for. Same disposal path as the vert-count
      // mismatch above; OGSModel turns `calibrationError` into the UI pill.
      //
      // Units are the BUNDLE's, which is not always centimetres — eyeline-v10c's
      // is metres — so the numbers are reported bare against a tolerance derived
      // from the same mesh, rather than labelled with a unit that would be wrong
      // half the time.
      this.calibrationError =
        `the rig does not fit this bundle's mesh (residual ${resid.toExponential(2)} vs tolerance `
        + `${tol.toExponential(2)}, bundle units, after scale+rotation+translation) — wrong DNA for `
        + `this character, or a vertex-order mismatch`;
      console.error(`[ogs] branch '${this.name}': ${this.calibrationError} — live pose disabled`);
      this.deform.dispose();
      this.deform = null;
      return false;
    }
    if (borrowedFrame && tol > 0 && resid > tol) {
      // Not fatal, but worth one line: a borrowed frame placing this branch far
      // from its baked neutral is how a genuinely mis-bound shell would look too.
      console.warn(`[ogs] branch '${this.name}': borrowed rig space places it `
        + `${(resid / tol).toFixed(1)}x past the head's own tolerance — shells still driven`);
    }
    this.corr = corr;
    this.anchorLin = lin;
    this.anchorOffset = offset;
    this.baseAnchorLin = Float32Array.from(lin);
    this.baseAnchorOffset = Float32Array.from(offset);
    this.anchored = true;
    // GPU path: fold both into the existing vert-transform pass
    // (v' = v·M + offset + corr[i]).
    if (this.lifter?.setVertCorrection) {
      this.lifter.setDeformLinear?.(lin, offset);
      this.lifter.setVertCorrection(corr);
    }
    return true;
  }

  /** The last `pose` — its rig, and where the deform output it produced ended up. Held
   *  so a pose that only moves the ANCHOR can re-run the transform without re-running
   *  the deform.
   *
   *  `tensor` is ORT's own output and carries ORT's reused-buffer contract: valid until
   *  the next inference, after which the arena may hand that memory to another node. So
   *  it is read ONCE, on the pose that produced it, and a re-anchor works from the
   *  lifter's own snapshot instead (GpuLifter.reapplyPose) — reading it a frame later is
   *  what stretched a band of head splats into needles during a walk. */
  private lastPosed: { rig: Float32Array; tensor: NodeTensor } | null = null;
  /** Reused copy of the last posed rig. `pose()` runs on every lip-sync frame, and a
   *  fresh 168-float copy per call is exactly the per-frame allocation the render loop
   *  forbids — the buffer is written in place instead. */
  private lastPosedRig: Float32Array | null = null;

  /**
   * Pose at `rig`, reusing the last deform output when the rig has not changed.
   *
   * The body drive moves the head's ANCHOR every frame (it rides the head bone, see
   * partBodyPose.js) while the FACE rig sits still between lip-sync frames — and the
   * anchor only reaches the lifter through the transform pass inside a pose. Re-running
   * the deform for it costs a full rig2mesh/ORL evaluation (measured 5 ms of a 17 ms
   * body frame) to compute vertices identical to the ones already on the device.
   *
   * A CHANGED rig falls through to a full pose, so this can never show a stale face.
   */
  async reanchor(rig: Float32Array): Promise<void> {
    const last = this.lastPosed;
    if (!last || !this.lifter || !sameRig(last.rig, rig)) return this.pose(rig);
    // From the lifter's OWN copy of those vertices, never from ORT's output buffer: the
    // pose that produced it may be many frames back, and that memory is ORT's to reuse.
    // reapplyPose puts the anchored deform output back, which is exactly the input the
    // body skinning consumes — so the two always run as a pair, and the skinning is never
    // applied twice to its own result.
    if (last.tensor.loc === "gpu-buffer" && this.lifter.reapplyPose?.()) { this.bodySkin?.(); return; }
    // No snapshot to re-transform (CPU verts, or a lifter without the fast path): the
    // verts sit at the OLD anchor, so take the full pose rather than present a wrong one.
    return this.pose(rig);
  }

  /** Live pose: rig → deform → the lifter's vert buffer. On the gpu-buffer path
   *  this is copyBufferToBuffer → transform(+corr) → jacobians in ONE
   *  submission, with no readback. Requires calibrate(). */
  async pose(rig: Float32Array): Promise<void> {
    if (!this.deform) return;
    if (!this.anchored) throw new Error(`OGSBranch "${this.name}": calibrate before pose`);
    if (!this.lifter) throw new Error(`OGSBranch "${this.name}": initGpu before pose`);
    const t = await this.deform.pose(rig);
    if (!this.lastPosedRig || this.lastPosedRig.length !== rig.length) {
      this.lastPosedRig = new Float32Array(rig.length);
    }
    this.lastPosedRig.set(rig);
    this.lastPosed = this.lastPosed && this.lastPosed.rig === this.lastPosedRig
      ? ((this.lastPosed.tensor = t), this.lastPosed)
      : { rig: this.lastPosedRig, tensor: t };
    if (t.loc === "gpu-buffer" && this.lifter.setPoseGpu) {
      this.lifter.setPoseGpu(nodeTensorToLiftInput(t) as { gpuBuffer: GPUBuffer; byteLength: number });
      this.bodySkin?.();
      return;
    }
    // CPU fallback: the EP ignored the gpu-buffer hint, the device was demoted,
    // or ?gpudeform=0. Loud once — this is a real per-frame upload cliff.
    if (this.lifter.setPoseGpu && !this.warnedCpuVerts) {
      this.warnedCpuVerts = true;
      console.warn(`[ogs] branch '${this.name}': deform verts came back on CPU (EP ignored the gpu-buffer hint) — per-frame upload path`);
    }
    const src = t.loc === "cpu" ? t.data : await this.deform.download(t);
    const v = this.verts;
    if (src.length !== v.length) {
      if (!this.warnedVertCount) {
        this.warnedVertCount = true;
        console.warn(`[ogs] branch '${this.name}': deform verts ${src.length / 3} ≠ mesh ${v.length / 3} — pose skipped`);
      }
      return;
    }
    // Same transform the WGSL pass applies: v·M + offset + corr. Kept in step
    // with vert_transform.wgsl by hand — the two are gated against each other by
    // tests/unit/ogsBranchLive.test.mjs (CPU) and the shader's own row-vector
    // comment; a divergence here is a silently different face on the CPU path.
    const corr = this.corr, m = this.anchorLin, o = this.anchorOffset;
    for (let i = 0; i < v.length; i += 3) {
      const x = src[i], y = src[i + 1], z = src[i + 2];
      v[i]     = x * m[0] + y * m[3] + z * m[6] + o[0] + (corr ? corr[i] : 0);
      v[i + 1] = x * m[1] + y * m[4] + z * m[7] + o[1] + (corr ? corr[i + 1] : 0);
      v[i + 2] = x * m[2] + y * m[5] + z * m[8] + o[2] + (corr ? corr[i + 2] : 0);
    }
    this.lifter.computeJacobians(v);
  }

  /** rig → trunk → code → geom → geom_uv. Caches `code` inside OGSAvatar.
   *
   *  Also offers the head DISPLACEMENT (verts_t - verts_0) when this branch has
   *  posed CPU vertices. Whether it is CONSUMED is the trunk graph's call: a rig
   *  trunk declares `rig_params` and never sees it, a mesh-conditioned head
   *  (TexAvatars --head_mesh_cond) declares `head_disp` and takes it instead. */
  async runGeom(rig: Float32Array): Promise<NodeTensor> {
    const avatar = this.requireAvatar();
    const rigT: NodeTensor = { loc: "cpu", data: rig, dims: [1, this.rigDim] };
    // A local_disp head is conditioned on the displacement UV MAP, not the vector.
    const disp = this.isLocalDisp ? this.headDispMapTensor() : this.headDispTensor();
    const { geom_uv } = await avatar.runGeom(this.frame(null), rigT, disp);
    this.lastGeom = geom_uv;
    return geom_uv;
  }

  /** True for a TexAvatars --head_local_disp head (scene.json `head_cond: "local_disp"`). */
  get isLocalDisp(): boolean {
    return this.live?.assets.spec?.headCond === "local_disp";
  }

  /** (1,3,H,W) per-texel displacement UV map for a local_disp head — the browser
   *  twin of ae/local_displacement.py's `displacement_uv_map` + `soft_clip`, built
   *  from THIS branch's posed vertices against its rest pose, into its own atlas.
   *  Null under the GPU deform (same trap and same answer as headDispTensor: the
   *  CPU verts are the neutral there, so the map would be zero — a frozen face).
   *  Everything that would make the field a DIFFERENT function from the trainer's
   *  is refused loudly rather than approximated: a rigid-aligned run (no Kabsch
   *  on this path), an atlas that is not the decoders' resolution (the field is
   *  never resampled), a mesh whose arrays disagree in shape. */
  private headDispMapTensor(): NodeTensor | null {
    const live = this.live;
    if (!live || !this.restVerts) return null;
    const spec = live.assets.spec;
    const mesh = live.assets.mesh;
    const disp = spec.disp ?? { uvRes: this.uvRes, rigidAlign: false, clipMm: 0 };
    if (disp.rigidAlign) {
      throw new Error(`OGSBranch "${this.name}": this head was trained with head_disp_rigid_align (the fusion `
        + `saw the Kabsch-aligned, expression-only field) and the browser rasterises the RAW verts - rest `
        + `field — feeding it would condition the decoders on the head's world pose, which they never saw. `
        + `Export from a run trained with --head_disp_rigid_align 0, or add the alignment to headDisplacementMap.js.`);
    }
    const H = disp.uvRes, W = disp.uvRes;
    if (mesh.manifest.uv_res !== H || mesh.triim.length !== H * W || mesh.barim.length !== H * W * 3) {
      throw new Error(`OGSBranch "${this.name}": the mesh atlas is ${mesh.manifest.uv_res}² (triim ${mesh.triim.length}) `
        + `but the decoders take a ${H}² disp_map (scene.json disp.uv_res) — the field is fed dense and never `
        + `resampled, so the exporter must write the atlas at the decoders' resolution`);
    }
    if (this.gpuDeformActive()) {
      if (!this.reportedDispGpu) {
        this.reportedDispGpu = true;
        console.warn(
          `[ogs] ${this.id}: the local_disp head's disp_map needs CPU-posed vertices, but the GPU deform ` +
          `keeps branch.verts at the neutral pose — run with ?gpudeform=0, or the decoders see a zero ` +
          `field and render one frozen expression.`,
        );
      }
      return null;
    }
    const out = buildHeadDispMap({
      vertsT: this.verts, vertsRest: this.restVerts,
      triim: mesh.triim, barim: mesh.barim, faces: mesh.faces,
      H, W, clipMm: disp.clipMm,
    }, this.dispMapBuf);
    if (!out) {
      throw new Error(`OGSBranch "${this.name}": disp_map inputs disagree in shape (verts ${this.verts.length / 3}, `
        + `rest ${this.restVerts.length / 3}, triim ${mesh.triim.length}, barim ${mesh.barim.length / 3}, atlas ${H}x${W})`);
    }
    this.dispMapBuf = out;
    this.lastDispMap = out;
    return { loc: "cpu", data: out, dims: [1, 3, H, W] };
  }

  /** (1, V, 3) displacement from the rest pose, or null when this branch cannot
   *  produce one. Null is the normal case for every rig-conditioned bundle.
   *
   *  REFUSES THE GPU DEFORM PATH, LOUDLY. On `gpuDeform` the posed vertices go
   *  straight to the lifter as a GPUBuffer and `this.verts` stays at its NEUTRAL
   *  pose, so the displacement computed from it would be exactly zero -- a face
   *  frozen at rest, every graph running fine and no error anywhere. That is the
   *  same trap `?meshview` documents for the branch mesh overlay, and the same
   *  answer: force the CPU deform rather than read an array that answers the
   *  question with a confident lie. */
  /** True when the deform writes posed verts straight to the lifter as a
   *  GPUBuffer, leaving `this.verts` at the neutral pose. */
  private gpuDeformActive(): boolean {
    return !!this.flags.gpuDeform && !!this.deform?.vertsBuffer;
  }

  private headDispTensor(): NodeTensor | null {
    if (!this.verts || !this.restVerts) return null;
    if (this.gpuDeformActive()) {
      if (!this.reportedDispGpu) {
        this.reportedDispGpu = true;
        console.warn(
          `[ogs] ${this.id}: head_disp needs CPU-posed vertices, but the GPU deform ` +
          `keeps branch.verts at the neutral pose — run with ?gpudeform=0 for a ` +
          `mesh-conditioned head, or its trunk decodes one frozen expression.`,
        );
      }
      return null;
    }
    this.dispBuf = computeDisplacement(this.verts, this.restVerts, this.dispBuf) ?? undefined;
    if (!this.dispBuf) return null;
    return { loc: "cpu", data: this.dispBuf, dims: [1, this.dispBuf.length / 3, 3] };
  }

  /** {cached code, plücker(camera)} → appr → color_uv. Requires runGeom first. */
  async runAppearance(camera: THREE.PerspectiveCamera): Promise<NodeTensor> {
    const avatar = this.requireAvatar();
    const { color_uv } = await avatar.runAppearance(this.frame(camera));
    this.lastColor = color_uv;
    return color_uv;
  }

  /** Full pass at `rig`: geom + appr. The LIFT is deliberately NOT here — the
   *  terminal OGSRenderer lifts every branch and presents ONCE, so a rig update
   *  costs one present AND one overlapped readback drain rather than one of each
   *  per branch (see OGSRenderer's header). */
  async renderFull(rig: Float32Array, camera: THREE.PerspectiveCamera): Promise<{ geom_uv: NodeTensor; color_uv: NodeTensor }> {
    const geom_uv = await this.runGeom(rig);
    const color_uv = await this.runAppearance(camera);
    return { geom_uv, color_uv };
  }

  /** Camera-only re-color: reuse the cached geom_uv, re-run plücker + appr. */
  async renderAppearance(camera: THREE.PerspectiveCamera): Promise<{ geom_uv: NodeTensor; color_uv: NodeTensor }> {
    if (!this.lastGeom) throw new Error(`OGSBranch "${this.name}": renderFull before renderAppearance`);
    const color_uv = await this.runAppearance(camera);
    return { geom_uv: this.lastGeom, color_uv };
  }

  /** The geom_uv of the last full pass, or null before one (also null right
   *  after a precision swap, which forces the next pass to be a full decode). */
  get lastGeomUv(): NodeTensor | null {
    return this.lastGeom;
  }

  /** Adopt maps produced OUTSIDE the decoders (a region lookup table,
   *  inference/regionLut.js) as this branch's current outputs, so every consumer
   *  of `uvOutputs()` — a re-present at a new body pose, a region toggle — lifts
   *  them rather than the last thing the decoders emitted. */
  setExternalOutputs(geom: NodeTensor, color: NodeTensor): void {
    this.lastGeom = geom;
    this.lastColor = color;
  }

  /** The decoder outputs of the last full pass, or null before one. */
  uvOutputs(): { geom_uv: NodeTensor; color_uv: NodeTensor } | null {
    if (!this.lastGeom || !this.lastColor) return null;
    return { geom_uv: this.lastGeom, color_uv: this.lastColor };
  }

  /** The same outputs as CPU arrays. No production caller today — kept because a
   *  WebGL-side consumer (spark DataTextures) can't take a WebGPU buffer, so
   *  anything rendering outside the WebGPU context needs this download.
   *  One-time (geom) / per-camera-change
   *  (color), never per frame. */
  async uvOutputsCpu(): Promise<{ geomUv: Float32Array; colorUv: Float32Array } | null> {
    const uv = this.uvOutputs();
    if (!uv) return null;
    const dl = async (t: NodeTensor): Promise<Float32Array> => {
      if (t.loc === "cpu") return t.data;
      return (await (t.tensor as unknown as { getData: (release?: boolean) => Promise<unknown> }).getData(false)) as Float32Array;
    };
    return { geomUv: await dl(uv.geom_uv), colorUv: await dl(uv.color_uv) };
  }

  /** 'GPU(shared)' | 'GPU(own)' | 'CPU' | 'none' — for the one-line branch log. */
  liftKind(): string {
    return this._liftKind;
  }

  /** 'GPU' | 'CPU' — the plücker's resolved mode. */
  pluckerKind(): string {
    const p = this.avatar?.pluckerNode as unknown as { mode?: () => string } | undefined;
    return p?.mode?.() ?? "CPU";
  }

  /** The plücker sub-node — the __ISAAC_GPU_PARITY__ debug readback. */
  get pluckerNode(): Node | null {
    return this.avatar?.pluckerNode ?? null;
  }

  /** The rig2mesh sub-node, or null when the branch has none. Exposed for the
   *  init-only CPU readback the single-region mean-offset learning needs. */
  get deformNode(): BranchDeform | null {
    return this.deform;
  }

  /** This branch's fitted rig->bundle frame, once calibrate() has run.
   *
   *  Published so a SIBLING branch can borrow it. The eyes/teeth branch cannot
   *  fit this for itself — its mesh is a decimated stand-in with no vertex
   *  correspondence to anything in the pack, so there is nothing to fit against —
   *  but every branch of a bundle is baked in ONE space, so the head's frame is
   *  not an approximation for it, it is the correct answer. */
  get rigSpace(): { lin: Float32Array; offset: Float32Array } | null {
    // The BASE fit, deliberately — not the live pair. A borrowing branch places
    // its shells in rig space ONCE, and the frame that placement is defined
    // against is the row-0 one. Handing it whatever pose delta happened to be
    // installed at that instant would bake a single arbitrary pose's correction
    // into the shells permanently.
    return this.anchored && this.baseAnchorLin && this.baseAnchorOffset
      ? { lin: this.baseAnchorLin, offset: this.baseAnchorOffset }
      : null;
  }

  /** Re-place this branch by a baked per-pose delta, in BUNDLE space.
   *
   *  `delta` is one transform for the whole bundle (see
   *  inference/poseAnchors.js): what varies pose to pose is the training
   *  export's `GlobalRigid(t)`, a single rigid fit of the entire baked mesh, so
   *  every branch takes the SAME delta on top of its own row-0 anchor and their
   *  relative placement is preserved exactly.
   *
   *  Cheap enough for the per-frame path: a 3x3 compose plus a 12-float uniform
   *  write. It touches neither `corr` (which is not pose-dependent — the
   *  rig-vs-wrap shape gap) nor the jacobians, so nothing is re-uploaded.
   *
   *  No-op before calibrate, and on a branch whose fit was refused: this only
   *  re-places a fit that already passed and can never resurrect one. */
  setPoseAnchorDelta(dLin: ArrayLike<number>, dOff: ArrayLike<number>): boolean {
    if (!this.anchored || !this.deform) return false;
    composeSimilarity(this.baseAnchorLin, this.baseAnchorOffset, dLin, dOff, this.anchorLin, this.anchorOffset);
    // The CPU pose path reads anchorLin/anchorOffset directly, so it needs
    // nothing further; the GPU path holds them in a uniform.
    this.lifter?.setDeformLinear?.(this.anchorLin, this.anchorOffset);
    return true;
  }

  /** True when this branch cannot fit its own frame and must be HANDED one
   *  (calibrate's second argument). The caller uses this to calibrate such
   *  branches last, after some sibling has published one. */
  get needsRigSpace(): boolean {
    return this.deform instanceof OrlShellDeform && !this.deform.isDriving;
  }

  /** The rest pose of this branch's control space: every control at zero. Used
   *  as the calibration anchor and for the initial neutral decode, before the
   *  face graph pushes a live rig. */
  /** The rig to decode a neutral at: the branch's reference rig (row 0 of its rig params)
   *  when the bundle ships one, else zeros — correct only for a space centred on 0. */
  neutralRig(): Float32Array {
    const reference = this.live?.assets.referenceRig;
    return reference ? new Float32Array(reference) : new Float32Array(this.rigDim);
  }

  get neutralVertices(): Float32Array {
    return this.live?.assets.mesh.neutralVertices ?? new Float32Array(0);
  }

  /** The branch's mesh assets (UVs + topology). Null for a pure grouping node. */
  get mesh(): BranchAssets["mesh"] | null {
    return this.live?.assets.mesh ?? null;
  }

  get rigDim(): number {
    return this.live?.assets.rigDim ?? 0;
  }

  get uvRes(): number {
    return this.live?.assets.uvRes ?? 0;
  }

  /** The resolution the decoders emit at (and the plücker is generated at) — `decoder_res`
   *  when the bundle declares it, else uvRes. The lift resamples the gap (uvResize.ts). */
  get decoderRes(): number {
    return this.live?.assets.spec?.decoderRes ?? this.uvRes;
  }

  get hasDeform(): boolean {
    return !!this.deform;
  }

  /** WHICH rig actually posed this branch, for the one-line ready log. Resolved
   *  from the built node rather than the plan: the plan is what was intended,
   *  and a lazy fallback can change the answer after it. */
  get deformKind(): "orl" | "shell" | "rig2mesh" | "none" {
    if (this.deform instanceof OrlDeform) return "orl";
    if (this.deform instanceof OrlShellDeform) return "shell";
    if (this.deform instanceof OGSRig2Mesh) return "rig2mesh";
    return "none";
  }

  get geomUsedFp16(): boolean {
    return !!this.avatar?.geomUsedFp16;
  }

  get apprUsedFp16(): boolean {
    return !!this.avatar?.apprUsedFp16;
  }

  /** Live fp16↔fp32 swap (the Avatar node's dropdown). Drops the cached decoder
   *  outputs so the next pass is a full decode. Caller owns the mutex. */
  async setPrecision(preferFp16: boolean): Promise<void> {
    await this.avatar?.setPrecision(preferFp16);
    // A precision swap installs a DIFFERENT DecoderSession (built earlier, or
    // pulled warm from PrecisionCache), and `downloadOutputs` is per-session and
    // defaults to false. On a DEMOTED device that silently re-arms gpu-buffer
    // outputs on ORT's device while the lifter lives on another one, and the
    // next encodeLift copies a foreign GPUBuffer → WebGPU validation error, dead
    // avatar. Re-assert the demote against whatever is now active.
    if (this.downloadOutputs) this.setDownloadOutputs(true);
    this.lastGeom = null;
    this.lastColor = null;
  }

  /** Build + cache the OTHER precision WITHOUT activating it, so the first
   *  fp16↔fp32 toggle is an instant swap. Best-effort background warm-up. */
  async prebuild(preferFp16: boolean): Promise<void> {
    await this.avatar?.prebuild(preferFp16);
  }

  /** Demote every session's gpu-buffer outputs to CPU (gpuDevice's `demoted`):
   *  the hints are baked in at create time, so a device mismatch discovered
   *  afterwards can only be repaired by downloading. STICKY — remembered so a
   *  later precision swap or prebuild can't quietly re-arm gpu-buffer outputs on
   *  a device the lifter can't read (see setPrecision). */
  setDownloadOutputs(on: boolean): void {
    this.downloadOutputs = on;
    for (const s of this.sessions()) s.setDownloadOutputs(on);
  }

  /** Every live DecoderSession behind this branch (the demote path). */
  sessions(): { setDownloadOutputs(on: boolean): void }[] {
    const out = this.avatar ? [...this.avatar.sessions()] : [];
    const r = this.deform?.activeSession;
    if (r) out.push(r);
    return out;
  }

  private requireAvatar(): OGSAvatar {
    if (!this.avatar) throw new Error(`OGSBranch "${this.name}": not a live branch (no inference chain)`);
    return this.avatar;
  }

  // One small object per pipeline pass (NOT per render frame) — the decoders
  // ignore it; only the plücker reads `camera`.
  private frame(camera: THREE.PerspectiveCamera | null): FrameInputs {
    return { rig: null, camera: camera as THREE.PerspectiveCamera, rigEpoch: 0, cameraEpoch: 0 };
  }

  // Pass-through: the member chain's terminal outputs flow through unchanged.
  async evaluate(
    _frame: FrameInputs,
    inbound: Readonly<Record<string, NodeTensor>>,
  ): Promise<Record<string, NodeTensor>> {
    const out: Record<string, NodeTensor> = {};
    for (const p of this.outputs) {
      const t = inbound[p.name];
      if (!t) throw new Error(`OGSBranch "${this.id}": missing branch output "${p.name}"`);
      out[p.name] = t;
    }
    return out;
  }

  // The member decoder nodes this branch resolves to — "broken into" the chain.
  brokenInto(): readonly string[] {
    return this.members;
  }

  dirtyOn(): readonly DirtySource[] {
    return []; // dirty only via its upstream member chain
  }

  dispose(): void {
    this.deform?.dispose();
    this.deform = null;
    this.avatar?.dispose();
    this.avatar = null;
    try { this.lifter?.destroy?.(); } catch { /* best-effort */ }
    this.lifter = null;
    this.lastGeom = null;
    this.lastColor = null;
  }
}

// Concatenate per-branch Gaussians into one set for a single rasterizer call
// (multi_part_gaussian_model.py concatenates head+body outputs). Pure.
export function concatGaussians(parts: readonly Gaussians[]): Gaussians {
  const total = parts.reduce((n, g) => n + g.count, 0);
  const out: Gaussians = {
    positions: new Float32Array(total * 3),
    scales: new Float32Array(total * 3),
    rotations: new Float32Array(total * 4),
    colors: new Float32Array(total * 3),
    opacities: new Float32Array(total),
    count: total,
  };
  let v3 = 0, v4 = 0, v1 = 0;
  for (const g of parts) {
    out.positions.set(g.positions.subarray(0, g.count * 3), v3);
    out.scales.set(g.scales.subarray(0, g.count * 3), v3);
    out.colors.set(g.colors.subarray(0, g.count * 3), v3);
    out.rotations.set(g.rotations.subarray(0, g.count * 4), v4);
    out.opacities.set(g.opacities.subarray(0, g.count), v1);
    v3 += g.count * 3;
    v4 += g.count * 4;
    v1 += g.count;
  }
  return out;
}
